import React from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'

import {Grid, CircularProgress, Fade} from '@material-ui/core'
import FormHelperText from "@material-ui/core/FormHelperText";

import {
  FormCenterHandlerResource,
  OnboardingProtocolHandlerResource,
  QuestionnairesHandlerResource
} from '../../../../../../../../utils/api'
import { displayErrorSnackBar } from '../../../../../../../../components/SnackbarProvider/actions'
import ProtocolPreview from '../../../../../ProtocolPreview'
import {BankDocuments, DocumentsFilterModal} from './components'
import useStyles from './styles'
import FormLabelElement from "../../../formElement/LabelFormElement/LabelFormElement";
import CheckboxQuestion from "../../../question/CheckboxQuestion/CheckboxQuestion";
import {getErrorMessage, RenderStepLoadingMessage,} from "../../../../../../../../utils/utils";
import BanksHint from './BanksHint';
import {DepotDocumentIdentifier, DepotDocumentIdentifiersList, singleDepotSelected} from "./utils";
import {
  useEinzeltitelProcessConfirmationModalContext
} from "../../../../../EinzeltitelsProcessConfirmationProvider/EinzeltitelsProcessConfirmationProvider";
import {getFromStorage, setInStorage, STORAGE_TYPE} from "../../../../../../../../utils/storage";
import InfoCard from "../../DynamicStep/components/InfoCard";
import {DEFAULT_EMPTY_SECTION_MESSAGE} from "../../../../../../../../utils/constants";
import BaseDialog from "../../../../../../../../components/BaseDialog/BaseDialog";


export const GENERAL_DOCUMENTS_BANK_NAME = 'Allgemeine Dokumente'
export const GENERAL_DOCUMENTS_BANK_ID = 'general'

export const ONBOARDING_DEPOT_ID = 'onboarding' // We need predefined depot id for onboarding process, as we do not have correct depot at this point yet

export default connect()(function DocumentsDownload(props) {

  const {
    questions: [
      selectedDocuments,
      waysOfDistribution
    ],
    dataService,
    onAnswerChange,
    dispatch
  } = props;

  const classes = useStyles()
  const modalContext = useEinzeltitelProcessConfirmationModalContext();

  const [documentsData, setDocumentsData] = React.useState({
    data: selectedDocuments.documents || null,
    loading: false,
    errors: null
  })
  const [previewDocument, setPreviewDocument] = React.useState(undefined)
  const [previewIsVisible, setPreviewIsVisible] = React.useState(false)
  const [previewDocumentContent, setPreviewDocumentContent] = React.useState({
    content: undefined,
    error: undefined,
    loading: false
  })

  const [documentsToDownload, setDocumentsToDownload] = React.useState(
    DepotDocumentIdentifiersList.fromJSON(_.get(selectedDocuments.answer, 'download') || {}));
  const [documentsToSign, setDocumentsToSign] = React.useState(
    DepotDocumentIdentifiersList.fromJSON(_.get(selectedDocuments.answer, 'eSign') || {}));
  const [documentsToStore, setDocumentsToStore] = React.useState(
    DepotDocumentIdentifiersList.fromJSON(_.get(selectedDocuments.answer, 'store') || {}));

  const SETTERS = {
    download: setDocumentsToDownload,
    eSign: setDocumentsToSign,
    store: setDocumentsToStore
  }

  const [documentsFilterModal, setDocumentsFilterModal] = React.useState({
    open: false,
    bankId: undefined,
    objectType: undefined,
    data: {'data': []},
    selectedDocuments: []
  })

  React.useEffect(() => {
    if (previewDocument) {
      fetchPreviewDocumentContent()
    }
  }, [previewDocument])

  React.useEffect(() => {
    // if something is wrong with the document content preview must hide
    if(_.isNil(previewDocumentContent.content) && !previewDocumentContent.loading){
      setPreviewIsVisible(false)
    }
  }, [previewDocumentContent])

  React.useEffect(() => {
    onAnswerChange(selectedDocuments.uid, {
      download: documentsToDownload.toJSON(),
      eSign: documentsToSign.toJSON(),
      store: documentsToStore.toJSON()
    })
  }, [documentsToSign, documentsToDownload, documentsToStore]);

  React.useEffect(() => {

    (async () => {
      if (!documentsData.data) {
        return
      }

      const data = dataService.investedInstrumentsGroupedByCustodian;

      if (_.isEmpty(data)) {
        return;
      }

      const skipConfirmationsConfigurationStorageKey = `${dataService.user.broker_id}_einzeltitels_confirmations_configuration`;
      const skipConfirmationsConfiguration = getFromStorage(skipConfirmationsConfigurationStorageKey, STORAGE_TYPE.LOCAL) || {};

      for (let [custodianId, custodianData] of Object.entries(data)) {
        if (custodianData.hasEinzeltitels && custodianData.einzeltitelsSubmissionMessage && !skipConfirmationsConfiguration[custodianId]) {
          skipConfirmationsConfiguration[custodianId] = await modalContext.confirm(custodianData.einzeltitelsSubmissionMessage);
        }
      }

      setInStorage(skipConfirmationsConfigurationStorageKey, skipConfirmationsConfiguration)

    })()

  }, [documentsData])

  const isQuestionRequired = (question) => question.hasOwnProperty('optional') ? !question.optional : true

  const fetchPreview = async () => {

    // Form Center documents has id as a number
    if (_.isNumber(previewDocument.id)) {
      return await FormCenterHandlerResource.getBankDocumentContent(
        previewDocument.id, dataService.onboarding_uid,
        previewDocument.depotId != ONBOARDING_DEPOT_ID && previewDocument.depotId,
        previewDocument.depotId != ONBOARDING_DEPOT_ID && previewDocument.objectType)
    }

    switch(previewDocument.id) {
      case 'protocol': {
        let protocolParams = {
          'customer_id': dataService.customer_id,
          'onboarding_uid': dataService.onboarding_uid,
        }
        return await OnboardingProtocolHandlerResource.getProtocolContent(protocolParams)
      }
      case 'risk_profile': {
        let questionnaireParams = {
          'customer_id': dataService.customer_id,
          'session_id': dataService.onboarding_uid
        }
        return await OnboardingProtocolHandlerResource.getQuestionnaireContent(questionnaireParams)
      }
      case 'product_information': {
        let questionnaireParams = {
          'session_id': dataService.onboarding_uid,
        }
        return await OnboardingProtocolHandlerResource.getProductInformationContent(questionnaireParams)
      }
      case 'esg': {
        let esgParams = {
          'customer_id': dataService.customer_id,
          'session_id': dataService.onboarding_uid
        }
        return await OnboardingProtocolHandlerResource.getEsgDocumentContent(esgParams)
      }
      case 'online': {
        return await OnboardingProtocolHandlerResource.getBankOnlineDocumentContent(
          dataService.onboarding_uid, previewDocument.external_id, previewDocument.objectType !== GENERAL_DOCUMENTS_BANK_ID
            ? previewDocument.objectType
            : undefined)
      }
      case 'proof_of_guardianship': {
        return await QuestionnairesHandlerResource.getDocument(previewDocument.documents[0])
      }
      default: {
        let legitimationParams = {
          'customer_id': dataService.customer_id,
          'filename': previewDocument.name,
          'documents': previewDocument.documents
        }
        return await OnboardingProtocolHandlerResource.getLegitimationDocumentsContent(legitimationParams)
      }
    }

  }

  const fetchPreviewDocumentContent = async () => {
    try {

      setPreviewDocumentContent({
        loading: true,
        content: undefined,
        error: undefined
      })

      let response = await fetchPreview()

      setPreviewDocumentContent({
        content: response,
        loading: false,
        error: undefined
      })

    } catch (error) {
      setPreviewDocumentContent({
        loading: false,
        content: undefined,
        error
      })
      setPreviewDocument(undefined)
      let errorMsg = getErrorMessage(error) || '';
      dispatch(displayErrorSnackBar(`Beim Laden der Vorschau für "${previewDocument.name}" ist ein Fehler aufgetreten. ${errorMsg}`))
    }
  }

  const onDistributionSettingsChange = (documentId, distributionType, checked, bankId, objectType) => {

    const documentIdentifier = new DepotDocumentIdentifier(bankId, documentId, objectType)

    if (selectedDocuments.errors && _.get(selectedDocuments.errors, `${objectType}.${bankId}.${documentId}`, false)){
      delete selectedDocuments.errors[objectType][bankId][documentId]
    }

    SETTERS[distributionType]((prevDocumentIdentifiers) => prevDocumentIdentifiers.toggleDocumentIdentifier(documentIdentifier))

  }

  const onSetDocumentPreview = (document, depotId, objectType) => {
    setPreviewDocument({...document, depotId, objectType});
    setPreviewIsVisible(true) // when new document is selected preview should be shown
  };

  const getSelectedOptionalDocuments = (bankId, objectType) => {
    let selectedDocs = _.get(documentsData.data, `${objectType}.${bankId}.optional`, []).map(doc => doc.id)
    return _.get(documentsData.data, `${objectType}.${bankId}.filterData.data.[0].documents`, [])
      .filter(doc => selectedDocs.includes(doc.id) || documentsToStore.includes(new DepotDocumentIdentifier(bankId, doc.id, objectType)))
  }

  const onDocumentFilterModalSwitch = (bankId, objectType) => {
    if (bankId !== undefined) {
      setDocumentsFilterModal(prevState => ({
        open: !prevState.open,
        bankId,
        objectType,
        data: _.get(documentsData.data, `${objectType}.${bankId}.filterData`, {'data': []}),
        selectedDocuments: getSelectedOptionalDocuments(bankId, objectType)
      }))
    } else {
      setDocumentsFilterModal({
        open: false,
        bankId: undefined,
        objectType: undefined,
        data: {'data': []},
        selectedDocuments: []
      })
    }
  }

  const onConfirmDocumentsFilter = (bankId, documents, objectType) => {
    let bankData = documentsData.data[objectType][bankId]

    bankData.optional = [
      ...bankData.optional,
      ...bankData.allOptional.filter(doc => documents.includes(doc.id))
    ]

    documents.forEach(docId => {
      SETTERS['store']((prevDocumentIdentifiers) => {
        return prevDocumentIdentifiers.toggleDocumentIdentifier(new DepotDocumentIdentifier(bankId, docId, objectType))
      })
    })

    setDocumentsData(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        [objectType]: {
          ...prevState.data[objectType],
          [bankId]: bankData
        }
      }
    }))
  }

  const onDeleteOptionalDocument = (bankId, documentId, objectType) => {
    documentsData.data[objectType][bankId].optional = _.get(documentsData.data, `${objectType}.${bankId}.optional`)
      .filter(doc => doc.id !== documentId)

    for (let distributionType of Object.keys(SETTERS)) {
      SETTERS[distributionType]((prevDocumentIdentifiers) => prevDocumentIdentifiers.remove(new DepotDocumentIdentifier(bankId, documentId, objectType)))
    }

    setDocumentsData(prevState => ({
      ...prevState,
      ...documentsData
    }))
  }

  if(documentsData.loading){
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress className={classes.loadingIndicator}/>
        {RenderStepLoadingMessage(_.get(dataService, 'step'))}
      </div>
    );
  }

  return (
    <Grid container spacing={3}>
      {!!waysOfDistribution && (
        <>
          <Grid item xs={12}>
            <FormLabelElement
              error={waysOfDistribution.error}
              label={waysOfDistribution.question_text}
              required={isQuestionRequired(waysOfDistribution)}
              customClasses={{labelRoot: 'bold'}}
            />
            {waysOfDistribution.error && (
              <FormHelperText error={true}>{waysOfDistribution.error}</FormHelperText>
            )}
            {waysOfDistribution.question.map(q => (
              <Grid key={q.uid} item xs={12} className={classes.checkboxRegularContainer}>
                <CheckboxQuestion
                  question={q}
                  onAnswerChange={(uid, value) => {
                    waysOfDistribution.error = null; // clean error
                    onAnswerChange(uid, value);
                  }}
                />
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12}>
            <FormLabelElement
              label={`Zusätzlich zu den gewählten Zustelloptionen werden alle Dokumente in Ihre Postbox eingestellt.`}
              customClasses={{
                labelRoot: classes.helpText
              }}
            />
          </Grid>
        </>
      )}
      {!!documentsData.data ? (
        <>
          {renderBanksHint(documentsData, dataService)}
          <Grid item xs={12}>
            <Grid container>
              <BankDocuments
                banks={documentsData.data}
                setPreviewDocument={onSetDocumentPreview}
                onDocumentFilterModalSwitch={onDocumentFilterModalSwitch}
                onDeleteOptionalDocument={onDeleteOptionalDocument}
                downloadDocuments={documentsToDownload || []}
                eSignDocuments={documentsToSign || []}
                onDistributionSettingsChange={onDistributionSettingsChange}
                errors={selectedDocuments.errors}
                newDesign={dataService.isNewDesign}
              />
            </Grid>
          </Grid>
          <BaseDialog
            title={_.get(previewDocument, 'name')}
            open={previewIsVisible}
            handleClose={() => setPreviewIsVisible(false)}
            customClasses={{dialogContent: classes.previewDialogContent}}
            PaperProps={{style: {height: '100%'}}}
          >
            <div className={classes.previewModalBody}>
              {previewDocumentContent.loading && (
                <div className={classes.previewLoadingContainer}>
                  <CircularProgress color="primary" size={45}/>
                </div>
              )}
              <Fade
                in={!previewDocumentContent.loading && !!previewDocumentContent.content && !previewDocumentContent.error}
                timeout={400}
                unmountOnExit
              >
                <div>
                  <ProtocolPreview content={previewDocumentContent.content} width="100%" height="100%" />
                </div>
              </Fade>
            </div>
          </BaseDialog>
          <DocumentsFilterModal
            objectType={documentsFilterModal.objectType}
            bankId={documentsFilterModal.bankId}
            banksData={documentsFilterModal.data}
            open={documentsFilterModal.open}
            handleConfirm={onConfirmDocumentsFilter}
            handleSwitch={onDocumentFilterModalSwitch}
            activeDocuments={documentsFilterModal.selectedDocuments}
          />
        </>
      ) : (
        <Grid item xs={12}>
          <InfoCard
            isNewDesign={dataService.isNewDesign}
            cardType={'warning'}
            info_text={DEFAULT_EMPTY_SECTION_MESSAGE}
          />
        </Grid>
      )}
    </Grid>
  )
})

// in case we have more banks than 1, it will be rendered for each bank separately
const renderBanksHint = (documentsData, dataService) => {
  if(!documentsData.data)
    return

  if (!singleDepotSelected(documentsData.data)) {
    return
  }

  return Object.keys(documentsData.data).map((objectType) => {
    return _.filter(Object.keys(documentsData.data[objectType]), (bank_id) => bank_id != GENERAL_DOCUMENTS_BANK_ID)
      .map((bank_id) => (
        <Grid item xs={12} key={bank_id}>
          <BanksHint
            newDesign={dataService.isNewDesign}
            bankName={documentsData.data[objectType][bank_id].name}
            isBankOnline={documentsData.data[objectType][bank_id].isOnline}
            additionalBankText={documentsData.data[objectType][bank_id].additionalBankText}
            isIndividualAssetManagement={documentsData.data[objectType][bank_id].isIndividualAssetManagement}
          />
        </Grid>
      ))
  })
}
